import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Truck } from 'lucide-react';

const RATES_API = `${process.env.REACT_APP_BACKEND_URL}/api/equipment-rental-rates`;
export const BASIS = [['day', 'Daily'], ['week', 'Weekly'], ['month', 'Monthly']];
export const basisNoun = (b, n) => ({ day: 'day', week: 'week', month: 'month' }[b] || 'day') + (Number(n) === 1 ? '' : 's');
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

let cache = null;
export const loadEquipmentRates = async () => {
  if (cache) return cache;
  const r = await axios.get(RATES_API);
  cache = r.data.rates || [];
  return cache;
};

// 365-owned equipment from the 2026 rate sheet: pick the item, Daily / Weekly / Monthly, how many periods.
// Controlled: value = { rate_id, rate_basis, qty }; onChange gets the same plus { rate, name, unit_price }.
export default function EquipmentRatePicker({ value, onChange, onAdd, dark = false, compact = false, testid = 'eq' }) {
  const [rates, setRates] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => { loadEquipmentRates().then(setRates).catch(() => setRates([])); }, []);

  const v = { rate_basis: 'day', qty: 1, ...(value || {}) };
  const rate = rates.find(r => r.id === v.rate_id);
  const groups = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const list = ql ? rates.filter(r => `${r.category} ${r.name}`.toLowerCase().includes(ql)) : rates;
    return list.reduce((acc, r) => { (acc[r.category] = acc[r.category] || []).push(r); return acc; }, {});
  }, [rates, q]);

  const emit = (upd) => {
    const next = { ...v, ...upd };
    const r = rates.find(x => x.id === next.rate_id);
    onChange({ ...next, rate: r || null, name: r ? `${r.category} — ${r.name}` : '', unit_price: r ? Number(r[next.rate_basis] || 0) : 0 });
  };
  const price = rate ? Number(rate[v.rate_basis] || 0) : 0;
  const total = price * (Number(v.qty) || 0);
  const inp = dark
    ? 'bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-sm px-2 py-2 outline-none'
    : 'bg-white border border-slate-300 rounded-lg text-slate-900 text-sm px-2 py-2 focus:border-blue-500 outline-none';
  const label = dark ? 'text-[11px] text-slate-800 font-semibold' : 'text-[11px] text-slate-500';

  return (
    <div className="space-y-2" data-testid={`${testid}-picker`}>
      {!compact && (
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter equipment… (forklift, ram, welder)" className={`${inp} w-full`} data-testid={`${testid}-filter`} />
      )}
      <select value={v.rate_id || ''} onChange={e => emit({ rate_id: e.target.value })} className={`${inp} w-full`} data-testid={`${testid}-select`}>
        <option value="">Select 365 equipment…</option>
        {Object.entries(groups).map(([cat, items]) => (
          <optgroup key={cat} label={cat}>
            {items.map(r => <option key={r.id} value={r.id}>{r.name} — {money(r.day)}/day · {money(r.week)}/wk · {money(r.month)}/mo{r.rate_year && r.rate_year !== 2026 ? ` (${r.rate_year} rate)` : ''}</option>)}
          </optgroup>
        ))}
      </select>
      <div className="flex gap-2 items-end flex-wrap">
        <div className="flex rounded-lg overflow-hidden border border-slate-300" data-testid={`${testid}-basis`}>
          {BASIS.map(([b, lbl]) => (
            <button key={b} type="button" onClick={() => emit({ rate_basis: b })}
              className={`px-3 py-2 text-xs font-semibold ${v.rate_basis === b ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`}
              data-testid={`${testid}-basis-${b}`}>
              {lbl}{rate ? <span className="block text-[10px] font-normal opacity-80">{money(rate[b])}</span> : null}
            </button>
          ))}
        </div>
        <div>
          <label className={label}>How many {basisNoun(v.rate_basis, 2)}</label>
          <input type="number" min="0.5" step="0.5" value={v.qty} onChange={e => emit({ qty: e.target.value })} className={`${inp} w-24 text-right font-mono`} data-testid={`${testid}-qty`} />
        </div>
        <div className="flex-1 min-w-[120px] text-right">
          <p className={label}>Charge</p>
          <p className={`font-mono font-bold ${dark ? 'text-slate-900' : 'text-slate-900'}`} data-testid={`${testid}-total`}>{rate ? money(total) : '—'}</p>
        </div>
        {onAdd && (
          <button type="button" disabled={!rate || !(Number(v.qty) > 0)} onClick={() => onAdd({ ...v, qty: Number(v.qty), rate, name: `${rate.category} — ${rate.name}`, unit_price: price })}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-semibold inline-flex items-center gap-1" data-testid={`${testid}-add`}>
            <Truck className="w-4 h-4" /> Add
          </button>
        )}
      </div>
    </div>
  );
}
